import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { Modal, TextAreaField, TextField } from '@app/components';
import { ProjectCreationDto } from '@app/hooks';
import { sharedMessages } from '@app/translations';

const nameFieldMaxLength = 255;
const descriptionFieldMaxLength = 300;

interface CreateNewProjectFormProps {
  values?: {
    name: string;
    description: string;
  };
  onClose: () => void;
  onSubmit: (values: ProjectCreationDto) => void;
}

const cnBem = createBemBlockBuilder(['createNewProjectModal']);

export const ProjectModalForm: FC<CreateNewProjectFormProps> = ({
  children,
  values,
  onClose,
  onSubmit,
}) => {
  const { $t } = useIntl();
  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    control,
  } = useForm({ mode: 'onTouched' });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Body>
        {children}
        <TextField
          className={cnBem('__name')}
          name="name"
          label={$t(sharedMessages.name)}
          control={control}
          maxLength={nameFieldMaxLength}
          rules={{ required: $t(sharedMessages.required) }}
          characterLimit={nameFieldMaxLength}
          defaultValue={values?.name}
        />
        <TextAreaField
          className={cnBem('__description')}
          name="description"
          label={$t({
            id: 'projects.descriptionOptional',
            defaultMessage: 'Description (optional)',
          })}
          control={control}
          characterLimit={descriptionFieldMaxLength}
          maxLength={descriptionFieldMaxLength}
          defaultValue={values?.description}
        />
      </Modal.Body>
      <Modal.Footer className={cnBem('__footer')}>
        <Button
          title={$t(sharedMessages.cancel)}
          styleType="text"
          onClick={onClose}
        >
          {$t(sharedMessages.cancel)}
        </Button>
        <Button
          styleType="default"
          type="submit"
          disabled={!isValid || isSubmitting || !isDirty}
        >
          {isEmpty(values)
            ? $t(sharedMessages.create)
            : $t(sharedMessages.save)}
        </Button>
      </Modal.Footer>
    </form>
  );
};
